/**
 * @fileoverview Keyboard navigation utilities for the WebChat application.
 *
 * This module provides functions for keyboard-based navigation within the chat interface,
 * including moving focus to the first or last interactive elements and handling
 * special scroll behaviors for different device types and contexts.
 *
 * @author Palash
 * @version 1.0.0
 */

//#region Imports

import { scrollChatToTop, scrollChatToBottom, getInteractiveElements } from "./utils";
import { TopNavigationState, BottomNavigationState } from "../../types/a11y_types";

//#endregion

//#region Top Navigation

/**
 * Navigates to the first message or interactive element in the chat
 * @param navState Navigation state containing all required parameters
 * @returns {boolean} True if navigation was successful, false otherwise
 */
export function navigateToTop(navState: TopNavigationState): boolean {
	try {
		if (typeof jQuery === "undefined") return false;

		// Destructure navState for easier access
		const { prefixClassName, isIFrameLoaded, isTilesOpened, enableMessageNavigation } = navState;

		// Find the chat body element for scrolling context if not provided
		const chatBodyElement = <HTMLElement>document.querySelector(`.${prefixClassName}chatBody`);

		// If enableMessageNavigation is true, focus on the first chat section
		if (enableMessageNavigation) {
			// First try to find chat sections with tabindex="0"
			const chatSection = jQuery(`.${prefixClassName}chatBody main section[tabindex="0"]`);
			if (chatSection.length > 0) {
				const firstFocusableSection = <HTMLElement>chatSection[0];
				if (!firstFocusableSection) return false;
				// Use specialized scrolling function with options object
				scrollChatToTop({
					element: firstFocusableSection,
					isIFrameLoaded,
					isTilesOpened,
					chatBodyElement,
				});
				firstFocusableSection.focus();
				const showFocusClass = `${prefixClassName}show-focus`;
				firstFocusableSection.classList.add(showFocusClass);
				removeClassOnBlur(firstFocusableSection, showFocusClass);
				return true;
			}
		}
		// If enableMessageNavigation is false, focus on the first button in the first message
		else {
			// Find the first button in any message
			const webchatSectionBtn = jQuery(`.${prefixClassName}standalone section .${prefixClassName}btn`);
			if (webchatSectionBtn.length > 0) {
				const firstFocusableSectionButton = <HTMLElement>webchatSectionBtn[0];
				if (!firstFocusableSectionButton) return false;
				// Use specialized scrolling function with options object
				scrollChatToTop({
					element: firstFocusableSectionButton,
					isIFrameLoaded,
					isTilesOpened,
					chatBodyElement,
				});
				firstFocusableSectionButton.focus();
				return true;
			}

			// If no buttons exist, try to find any interactive element
			// This selector looks for: buttons, links, elements with tabindex >= 0, input, select, textarea
			const interactiveElements = getInteractiveElements(prefixClassName, false);
			if (interactiveElements && interactiveElements.length > 0) {
				const firstInteractiveElement = <HTMLElement>interactiveElements[0];
				if (!firstInteractiveElement) return false;
				// Use specialized scrolling function with options object
				scrollChatToTop({
					element: firstInteractiveElement,
					isIFrameLoaded,
					isTilesOpened,
					chatBodyElement,
				});
				firstInteractiveElement.focus();
				return true;
			}
		}

		// If no elements found but we have a chat body, at least scroll to top
		if (chatBodyElement) {
			chatBodyElement.scrollTop = 0;
			return true;
		}

		return false;
	} catch (ex) {
		console.error("[keyboard-navigation.ts] An error has occurred in navigateToTop:\n", ex);
		return false;
	}
}

/**
 * Attaches a 'blur' event listener to a given HTML element to remove a specified class.
 * The listener is automatically removed after the first blur event.
 *
 * @param element The HTML element to attach the blur listener to.
 * @param className The class name to remove on blur.
 * @returns {boolean} True if the listener was successfully attached, false otherwise.
 */
function removeClassOnBlur(element: HTMLElement, className: string): boolean {
	// Input Validation and Edge Cases ---
	if (!element || !className || typeof className !== "string") return false;

	// Trim whitespace from class name just in case
	const trimmedClassName = className.trim();

	try {
		const handleBlur = function () {
			// Check if the class still exists before attempting to remove it,
			// in case it was removed by another process or never added.
			if (element.classList.contains(trimmedClassName)) {
				element.classList.remove(trimmedClassName);
			}
			// Remove the listener itself after it has fired once
			element.removeEventListener("blur", handleBlur);
		};

		// Attach the event listener
		element.addEventListener("blur", handleBlur);
		return true; // Indicate success
	} catch (error) {
		//console.error(`removeClassOnBlur Error: Failed to attach blur listener to element.`, element, error);
		return false; // Indicate failure
	}
}

//#endregion

//#region Bottom Navigation

/**
 * Navigates to the input box or the last interactive element in the chat
 * @param navState Navigation state containing all required parameters
 * @returns {boolean} True if navigation was successful, false otherwise
 */
export function navigateToBottom(navState: BottomNavigationState): boolean {
	try {
		if (typeof jQuery === "undefined") return false;

		// Destructure navState for easier access
		const { prefixClassName, isIFrameLoaded, isTilesOpened, disableInput } = navState;

		// Find the chat body element for scrolling context
		const chatBodyElement = <HTMLElement>document.querySelector(`.${prefixClassName}chatBody`);

		// If input is not disabled, focus on the chat textarea
		if (!disableInput) {
			// First try to find the textarea in the footer
			const textareaInput = jQuery(`.${prefixClassName}textAreaInput`);
			if (textareaInput.length > 0) {
				const inputElement = <HTMLElement>textareaInput[0];
				if (!inputElement) return false;
				scrollChatToBottom({
					element: inputElement,
					isIFrameLoaded,
					isTilesOpened,
					chatBodyElement,
				});
				inputElement.classList.add(`${prefixClassName}keyboard-focus`);
				inputElement.focus();
				return true;
			}
		}

		// If input is disabled or not found, try to find the last interactive element
		// Focus on the last button (such as a quick reply option)
		const interactiveButtons = jQuery(`.${prefixClassName}standalone section .${prefixClassName}btn:visible`);
		if (interactiveButtons.length > 0) {
			const lastButton = <HTMLElement>interactiveButtons[interactiveButtons.length - 1];
			if (!lastButton) return false;
			// Use specialized scrolling function with options object
			scrollChatToBottom({
				element: lastButton,
				isIFrameLoaded,
				isTilesOpened,
				chatBodyElement,
			});
			lastButton.focus();
			return true;
		}

		// If no buttons found, try to find any interactive element at the bottom
		// This selector looks for: buttons, links, elements with tabindex >= 0, input, select, textarea
		const allInteractiveElements = getInteractiveElements(prefixClassName, true);
		if (allInteractiveElements && allInteractiveElements.length > 0) {
			const lastInteractiveElement = <HTMLElement>allInteractiveElements[allInteractiveElements.length - 1];
			if (!lastInteractiveElement) return false;
			// Use specialized scrolling function with options object
			scrollChatToBottom({
				element: lastInteractiveElement,
				isIFrameLoaded,
				isTilesOpened,
				chatBodyElement,
			});
			lastInteractiveElement.focus();
			return true;
		}

		// If no interactive elements found, just scroll to the bottom
		if (chatBodyElement) {
			chatBodyElement.scrollTop = chatBodyElement.scrollHeight;
			return true;
		}
		return false;
	} catch (ex) {
		console.error("[keyboard-navigation.ts] An error has occurred in navigateToBottom:\n", ex);
		return false;
	}
}

//#endregion
